import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Input,
  SimpleGrid,
  useColorModeValue,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import SupplierList from '../components/SupplierList';
import SupplierModals from '../components/SupplierModals';
import ConfirmationModal from '../components/ConfirmationModal';
import DebtModal from '../components/DebtModal';
import usePagination from '../utils/usePagination';
import api from '../api';

const Suppliers = () => {
  // Color mode values for modern styling
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const subtleTextColor = useColorModeValue('gray.600', 'gray.400');
  const shadowColor = useColorModeValue('rgba(0, 0, 0, 0.1)', 'rgba(0, 0, 0, 0.3)');
  const filterBg = useColorModeValue('white', 'gray.800');
  
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone_number: '',
    edrpou_code: '',
    address: '',
    bank_name: '',
    bank_number: '',
  });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDebtModalOpen, setIsDebtModalOpen] = useState(false);

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  const fetchSuppliers = async (url = `/suppliers/?search=${search}`) => {
    try {
      const response = await api.get(url);
      setSuppliers(response.data.results);
      updatePagination(response.data); // Use pagination hook to update state
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    }
  };

  const { pagination, updatePagination, fetchNext, fetchPrevious, goToPage } = usePagination(fetchSuppliers, '/suppliers/');

  useEffect(() => {
    fetchSuppliers();
  }, [search]);

  const handleCreate = async () => {
    try {
      await api.post('/suppliers/', formData);
      fetchSuppliers();
      setIsCreateModalOpen(false);
    } catch (error) {
      console.error('Failed to create supplier:', error);
    }
  };

  const handleEdit = async () => {
    try {
      await api.put(`/suppliers/${formData.id}/`, formData);
      fetchSuppliers();
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Failed to edit supplier:', error);
    }
  };

  const handleOpenDeleteConfirm = (supplier) => {
    setSupplierToDelete(supplier);
    setIsConfirmationModalOpen(true);
  };

  const handleDeleteSupplier = async () => {
    if (!supplierToDelete) return;

    try {
      await api.delete(`/suppliers/${supplierToDelete.id}/`);
      fetchSuppliers();
    } catch (error) {
      console.error('Failed to delete supplier:', error);
    } finally {
      setIsConfirmationModalOpen(false);
      setSupplierToDelete(null);
    }
  };

  const handleViewDebt = (supplier) => {
    setSelectedSupplier(supplier);
    setIsDebtModalOpen(true);
  };

  const handleCloseDebtModal = () => {
    setIsDebtModalOpen(false);
    setSelectedSupplier(null);
    // Refresh suppliers to show updated debt amounts
    fetchSuppliers();
  };

  return (
    <Box position="relative" minH="100vh" bg={useColorModeValue('gray.50', 'gray.900')} overflowX="hidden">
      <Box position="relative" zIndex={1} p={{ base: 2, md: 8 }}>
        {/* Header Section - Modern Card */}
        <Box
          bg={cardBg}
          borderRadius={{ base: 'lg', md: '2xl' }}
          border="1px solid"
          borderColor={borderColor}
          p={{ base: 3, md: 8 }}
          mb={{ base: 4, md: 8 }}
          boxShadow={`0 20px 25px -5px ${shadowColor}, 0 10px 10px -5px ${shadowColor}`}
          position="relative"
          overflow="visible"
        >
          {/* Gradient accent */}
          <Box position="absolute" top={0} left={0} right={0} height="6px" bg="linear-gradient(90deg, #667eea, #764ba2, #f093fb, #f5576c)" />
          <Flex justify="space-between" align="center" wrap="wrap" gap={4} direction={{ base: 'column', md: 'row' }}>
            <Heading size={{ base: 'lg', md: 'xl' }} bgGradient="linear(to-r, #667eea, #764ba2)" bgClip="text" fontWeight="bold" textAlign={{ base: 'center', md: 'left' }}>
              🏭 Постачальники
            </Heading>
            <Button 
              leftIcon={<AddIcon />}
              bg="linear-gradient(135deg, #667eea, #764ba2)"
              color="white"
              size={{ base: 'md', md: 'lg' }}
              borderRadius="xl"
              px={{ base: 6, md: 8 }}
              boxShadow="0 4px 15px rgba(102, 126, 234, 0.4)"
              _hover={{ bg: 'linear-gradient(135deg, #5a6fd8, #6a4190)', transform: { base: 'none', md: 'translateY(-2px)' } }}
              _active={{ transform: 'translateY(0)' }}
              onClick={() => setIsCreateModalOpen(true)}
            >
              Додати постачальника
            </Button>
          </Flex>
        </Box>
        {/* Filters Section - Modern Card */}
        <Box 
          bg={filterBg}
          borderRadius={{ base: 'lg', md: 'xl' }}
          border="1px solid"
          borderColor={borderColor}
          p={{ base: 3, md: 6 }}
          mb={{ base: 4, md: 8 }}
          boxShadow={`0 10px 15px -3px ${shadowColor}`}
          position="relative"
          overflow="hidden"
        >
          <Box position="absolute" top={0} left={0} right={0} height="4px" bg="linear-gradient(90deg, #4facfe, #00f2fe)" />
          <HStack spacing={{ base: 2, md: 4 }} w="full">
            <Input
              placeholder="🔍 Пошук постачальників..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              bg={cardBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="lg"
              size={{ base: 'md', md: 'lg' }}
              _focus={{ borderColor: '#4facfe', boxShadow: '0 0 0 1px rgba(79, 172, 254, 0.3)' }}
              _placeholder={{ color: subtleTextColor }}
            />
          </HStack>
        </Box>
        {/* Supplier Grid - Modern Card Container */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={{ base: 4, md: 6 }} mt={4}>
          <SupplierList
            suppliers={suppliers}
            onEdit={(supplier) => { setFormData(supplier); setIsEditModalOpen(true); }}
            onDelete={handleOpenDeleteConfirm}
            onViewDebt={handleViewDebt}
          />
        </SimpleGrid>
        {/* Modern Pagination */}
        {pagination.count > 0 && (
          <Box bg={cardBg} borderRadius={{ base: 'lg', md: '2xl' }} border="1px solid" borderColor={borderColor} p={{ base: 4, md: 6 }} mt={{ base: 6, md: 8 }} boxShadow={`0 10px 15px -3px ${shadowColor}`}>
            <Flex justify="center" align="center">
              {pagination.count > 12 && (
                <HStack spacing={{ base: 2, md: 3 }} flexWrap="wrap" justify="center">
                  <Button onClick={() => fetchPrevious()} isDisabled={!pagination.previous} bg="linear-gradient(135deg, #667eea, #764ba2)" color="white" borderRadius="xl" size={{ base: 'sm', md: 'md' }} px={{ base: 3, md: 6 }} _hover={{ bg: 'linear-gradient(135deg, #5a6fd8, #6a4190)', transform: { base: 'none', md: 'translateY(-1px)' } }} _disabled={{ bg: 'gray.300', color: 'gray.500', transform: 'none' }}>
                    ← Попередня
                  </Button>
                  {Array.from({ length: Math.ceil(pagination.count / 12) }, (_, index) => (
                    <Button key={index + 1} onClick={() => goToPage(index + 1)} size={{ base: 'sm', md: 'md' }} minW={{ base: '36px', md: '40px' }} px={{ base: 2, md: 3 }} bg={pagination.currentPage === index + 1 ? 'linear-gradient(135deg, #f093fb, #f5576c)' : cardBg} color={pagination.currentPage === index + 1 ? 'white' : textColor} border="1px solid" borderColor={pagination.currentPage === index + 1 ? 'transparent' : borderColor} borderRadius="lg" _hover={{ bg: pagination.currentPage === index + 1 ? 'linear-gradient(135deg, #e881f9, #f3455a)' : borderColor, transform: { base: 'none', md: 'translateY(-1px)' } }}>
                      {index + 1}
                    </Button>
                  ))}
                  <Button onClick={() => fetchNext()} isDisabled={!pagination.next} bg="linear-gradient(135deg, #667eea, #764ba2)" color="white" borderRadius="xl" size={{ base: 'sm', md: 'md' }} px={{ base: 3, md: 6 }} _hover={{ bg: 'linear-gradient(135deg, #5a6fd8, #6a4190)', transform: { base: 'none', md: 'translateY(-1px)' } }} _disabled={{ bg: 'gray.300', color: 'gray.500', transform: 'none' }}>
                    Наступна →
                  </Button>
                </HStack>
              )}
            </Flex>
          </Box>
        )}
        {/* Modals */}
        <SupplierModals
          isCreateModalOpen={isCreateModalOpen}
          isEditModalOpen={isEditModalOpen}
          onCloseCreate={() => setIsCreateModalOpen(false)}
          onCloseEdit={() => setIsEditModalOpen(false)}
          onSubmitCreate={handleCreate}
          onSubmitEdit={handleEdit}
          formData={formData}
          setFormData={setFormData}
        />
        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          onClose={() => setIsConfirmationModalOpen(false)}
          onConfirm={handleDeleteSupplier}
          message="Ви впевнені, що хочете видалити цього постачальника?"
        />
        {/* Debt Modal */}
        <DebtModal
          isOpen={isDebtModalOpen}
          onClose={handleCloseDebtModal}
          supplier={selectedSupplier}
        />
      </Box>
    </Box>
  );
};

export default Suppliers;
